import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Surface } from "@/components/ds";
import { SafeImage } from "@/components/ds/SafeImage";
import {
  COLOR_KEYS, EMPTY_THEME, ThemeColorFields, ThemePresets, ThemePreviewCard,
  themeColorError, themeColorPayload,
} from "@/components/catalog/ThemeEditor";

const EMPTY = { ...EMPTY_THEME, headline: "", tagline: "", banners: [] };

/** Personalização visual da própria vitrine: cores e banners, nada de produto ou preço. */
export const CreatorCatalogSettings = ({ data, endpoint = "/creator/catalog-settings" }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    api
      .get(endpoint)
      .then(({ data: res }) => {
        const colors = Object.fromEntries(COLOR_KEYS.map((k) => [k, res.settings[k] || ""]));
        setForm({ ...EMPTY, ...res.settings, ...colors });
      })
      .catch((e) => toast.error(apiError(e)));
  }, [endpoint]);

  const applyPreset = ([key, , ...colors]) =>
    setForm((f) => ({ ...f, theme_preset: key,
                      ...Object.fromEntries(COLOR_KEYS.map((k, i) => [k, colors[i]])) }));

  const pickBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem muito grande (máximo 5 MB).");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data: res } = await api.post("/upload", body,
                                           { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, banners: [...f.banners,
                                        { image: res.path, title: "", subtitle: "",
                                          badge: "", active: true }] }));
      toast.success("Banner carregado. Salve para publicar na vitrine.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeBanner = (index) =>
    setForm((f) => ({ ...f, banners: f.banners.filter((_, i) => i !== index) }));

  const editBanner = (index, key, value) =>
    setForm((f) => ({ ...f, banners: f.banners.map((b, i) =>
      (i === index ? { ...b, [key]: value } : b)) }));

  const save = async () => {
    const error = themeColorError(form);
    if (error) return toast.error(error);
    setBusy(true);
    try {
      await api.put(endpoint, {
        ...themeColorPayload(form),
        headline: form.headline || "",
        tagline: form.tagline || "",
        banners: form.banners.map((b) => ({ image: b.image || null, title: b.title || "",
                                            subtitle: b.subtitle || "", badge: b.badge || "",
                                            active: b.active !== false })),
      });
      toast.success("Vitrine atualizada.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="creator-catalog-settings">
      <ThemePresets value={form.theme_preset} onApply={applyPreset}
                    testidPrefix="creator-theme" />

      <Surface className="p-5">
        <p className="font-display text-base">Cores da sua vitrine</p>
        <div className="mt-3">
          <ThemeColorFields form={form} testidPrefix="creator"
                            onChange={(key, value) => set(key)(value)} />
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Field label="Título da vitrine" hint="Aparece quando não há banner com imagem">
            <Input value={form.headline} onChange={(e) => set("headline")(e.target.value)}
                   maxLength={80} placeholder="Peças autorais da minha coleção"
                   data-testid="creator-headline" />
          </Field>
          <Field label="Frase de apoio">
            <Input value={form.tagline} onChange={(e) => set("tagline")(e.target.value)}
                   maxLength={160} placeholder="Feitas sob encomenda em impressão 3D"
                   data-testid="creator-tagline" />
          </Field>
        </div>
        <div className="mt-5">
          <ThemePreviewCard form={form} title={data?.creator?.display_name}
                            subtitle={form.headline} testid="creator-theme-preview" />
        </div>
      </Surface>

      <Surface className="p-5">
        <p className="font-display text-base">Banners da vitrine</p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          JPEG, PNG ou WebP · até 5 MB · recomendado 1600×600 px (proporção larga). Até 6 banners.
        </p>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp"
               className="hidden" onChange={pickBanner} data-testid="creator-banner-input" />
        <div className="mt-3 space-y-3">
          {form.banners.length === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="creator-banners-empty">
              Sem banner por enquanto: a vitrine usa o cabeçalho padrão.
            </p>
          )}
          {form.banners.map((b, i) => (
            <div key={b.image || i}
                 className="flex flex-col gap-3 rounded-xl border border-white/10 bg-secondary/30 p-3 sm:flex-row"
                 data-testid={`creator-banner-${i}`}>
              <SafeImage src={fileUrl(b.image)} alt="Banner"
                         wrapperClassName="h-20 w-full shrink-0 rounded-lg border border-white/10 sm:w-40"
                         fallbackLabel="sem imagem" />
              <div className="min-w-0 flex-1 space-y-2">
                <Input value={b.title} onChange={(e) => editBanner(i, "title", e.target.value)}
                       maxLength={80} placeholder="Título (opcional)"
                       data-testid={`creator-banner-title-${i}`} />
                <Input value={b.subtitle}
                       onChange={(e) => editBanner(i, "subtitle", e.target.value)}
                       maxLength={160} placeholder="Subtítulo (opcional)"
                       data-testid={`creator-banner-subtitle-${i}`} />
                <div className="flex items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <input type="checkbox" checked={b.active !== false}
                           onChange={(e) => editBanner(i, "active", e.target.checked)}
                           data-testid={`creator-banner-active-${i}`} />
                    Mostrar na vitrine
                  </label>
                  <Button size="sm" variant="outline"
                          onClick={() => removeBanner(i)}
                          data-testid={`creator-banner-remove-${i}`}
                          className="h-8 gap-1.5 rounded-full text-[11px] text-destructive">
                    <Trash2 className="h-3.5 w-3.5" /> Remover
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <Button variant="outline" disabled={uploading || form.banners.length >= 6}
                onClick={() => inputRef.current?.click()}
                data-testid="creator-banner-add-btn"
                className="mt-3 h-10 gap-2 rounded-full px-4">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" />
                     : <ImagePlus className="h-4 w-4" />} Enviar banner
        </Button>
      </Surface>

      <Button onClick={save} disabled={busy} data-testid="creator-catalog-save-btn"
              className="h-11 w-full gap-2 rounded-full font-semibold sm:w-auto sm:px-8">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Salvar vitrine
      </Button>
      <p className="text-[11px] text-muted-foreground">
        A personalização vale só para o seu catálogo
        {data?.creator?.slug ? ` (/${data.creator.slug})` : ""}. Preços, estoque e produtos
        continuam sob responsabilidade da TR3ND.
      </p>
    </div>
  );
};
